/**
 * Role-based permission matrix.
 * Platform Admin is platform-wide and not tier-gated.
 * Other roles are scoped within a single company account.
 */
export const PERMISSIONS = [
    // Account management
    { action: 'account.delete', roles: ['platform_admin', 'owner'] },
    { action: 'account.billing', roles: ['owner'] },
    { action: 'account.settings', roles: ['owner', 'admin'] },
    { action: 'account.users.manage', roles: ['owner', 'admin'] },
    { action: 'account.users.invite', roles: ['owner', 'admin'] },
    // Frameworks
    { action: 'frameworks.activate', roles: ['owner', 'admin'] },
    { action: 'frameworks.custom.create', roles: ['owner', 'admin'], tierRequired: 'enterprise' },
    // Deliverables
    { action: 'deliverables.view', roles: ['owner', 'admin', 'consultant', 'steward', 'viewer'] },
    { action: 'deliverables.edit', roles: ['owner', 'admin', 'consultant', 'steward'] },
    { action: 'deliverables.export', roles: ['owner', 'admin', 'consultant'] },
    // Assessments
    { action: 'assessment.view', roles: ['owner', 'admin', 'consultant', 'steward', 'viewer'] },
    { action: 'assessment.edit', roles: ['owner', 'admin', 'consultant', 'steward'] },
    // Registers & Operations
    { action: 'registers.view', roles: ['owner', 'admin', 'consultant', 'steward', 'viewer'] },
    { action: 'registers.edit', roles: ['owner', 'admin', 'consultant', 'steward'] },
    // File uploads
    { action: 'files.upload', roles: ['owner', 'admin', 'consultant', 'steward'] },
    { action: 'files.view', roles: ['owner', 'admin', 'consultant', 'steward', 'viewer'] },
    { action: 'files.delete', roles: ['owner', 'admin'] },
    // Snapshots
    { action: 'snapshots.create', roles: ['owner', 'admin'], tierRequired: 'pro' },
    { action: 'snapshots.view', roles: ['owner', 'admin', 'consultant', 'viewer'], tierRequired: 'pro' },
    // Auditor links
    { action: 'auditor_links.create', roles: ['owner', 'admin', 'consultant'], tierRequired: 'pro' },
    { action: 'auditor_links.revoke', roles: ['owner', 'admin'] },
    // Team & RACI
    { action: 'team.view', roles: ['owner', 'admin', 'consultant', 'steward', 'viewer'] },
    { action: 'team.assign', roles: ['owner', 'admin'] },
    // Platform Admin (not scoped to a single account)
    { action: 'platform.tenants.list', roles: ['platform_admin'] },
    { action: 'platform.tenants.view', roles: ['platform_admin'] },
    { action: 'platform.tenants.suspend', roles: ['platform_admin'] },
    { action: 'platform.tenants.delete', roles: ['platform_admin'] },
    { action: 'platform.leads.view', roles: ['platform_admin'] },
    { action: 'platform.leads.manage', roles: ['platform_admin'] },
    { action: 'platform.metrics.view', roles: ['platform_admin'] },
];
/** Roles available per pricing tier */
export const ROLES_BY_TIER = {
    free: [],
    starter: ['owner', 'viewer'],
    pro: ['owner', 'admin', 'consultant', 'steward', 'viewer'],
    enterprise: ['owner', 'admin', 'consultant', 'steward', 'viewer'],
};
/** Check if a role can perform an action */
export function hasPermission(role, action) {
    const perm = PERMISSIONS.find((p) => p.action === action);
    if (!perm)
        return false;
    return perm.roles.includes(role);
}
/** Check if a tier supports a specific role */
export function tierSupportsRole(tier, role) {
    if (role === 'platform_admin')
        return true;
    return ROLES_BY_TIER[tier]?.includes(role) ?? false;
}
//# sourceMappingURL=permissions.js.map